/**
 * usePostSyncSummaryToast
 *
 * Subscribes to the SSE `sync_completed` stream and surfaces a global toast
 * carrying the 4-counter import summary:
 *   - ✓ Imported:                 N
 *   - ↺ Skipped as duplicate:     N
 *   - ⚠ Needs attention:          N  → [View tray]
 *   - ✗ Not processable:          N  → "The team is working on it"
 *
 * The last two lines render ONLY when their counter > 0.
 *
 * Issue #322 B.3 — pairs with the "Needs attention" tray (Phase 4 B.2).
 *
 * Behavior:
 *   - Mounts once (intended to live in `AuthenticatedLayout`).
 *   - Dedupes per `jobId` so a replayed `sync_completed` event (reconnect,
 *     StrictMode double-mount) does NOT stack the same toast twice.
 *   - Skips events whose `summary` block is missing (legacy pre-#322 jobs).
 *   - Skips events whose all 4 counters are 0 (nothing useful to show).
 */

import { useEffect } from 'react'

import type { ImportJobSummary } from '@bat/shared/contracts/jobs/import-job.dto'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { resolveSyncSourceLabel } from '@/platform/sync/sync-source-label'
import type { SseSyncEvent } from '@/platform/sync/types'
import { syncClient } from '@/platform/websocket/syncClient'
import { useNotifications } from '@/shared/components/Notification'

import { hasChainHistoryGap } from '../components/ChainHistoryIncompleteNotice'
import { SyncSummaryToastContent } from '../components/SyncSummaryToastContent'
import { isImportOutcomeFullyWithheld } from '../utils/import-outcome-message'
import { createSeenJobIdTracker } from '../utils/seen-job-id-tracker'

/**
 * Module-level dedup tracker — survives StrictMode double-mounts and
 * re-renders. Bounded FIFO, see createSeenJobIdTracker.
 */
const seenJobIdTracker = createSeenJobIdTracker()

/**
 * True if the summary has at least one non-zero counter — otherwise we
 * skip the toast (nothing useful to show).
 */
function hasAnyCounter(summary: ImportJobSummary): boolean {
  return (
    summary.importedCount > 0 ||
    summary.skippedAsDuplicateCount > 0 ||
    summary.needsAttentionCount > 0 ||
    summary.unprocessableCount > 0 ||
    // Rows held for review are out of the ledger: the user must be told even if nothing else moved.
    (summary.heldForReviewCount ?? 0) > 0 ||
    // An import whose every row was withheld as cancelled writes nothing, but the user still
    // needs to be told WHY nothing landed — silence reads as a broken import.
    (summary.cancelledCount ?? 0) > 0 ||
    // A sync that ONLY surfaced reconciliation discrepancies (no other
    // counter) is still worth a toast so the operator is alerted.
    (summary.reconciliation?.discrepancyCount ?? 0) > 0 ||
    // Another source of the same platform already covers these dates: a
    // possible double count is worth a toast even when nothing else happened.
    summary.crossSourceOverlap !== undefined ||
    // The provider's history cap left part of the requested period out.
    (summary.coverageGaps?.length ?? 0) > 0 ||
    // #264: a wallet whose chain history was not read in full.
    hasChainHistoryGap(summary.chainHistoryIncomplete)
  )
}

export const usePostSyncSummaryToast = (): void => {
  const { show } = useNotifications()
  const navigate = useNavigate()
  // `monitoring.unknownSource` lives in the COMMON namespace.
  const { t: tCommon } = useTranslation('common')

  useEffect(() => {
    const handleSyncCompleted = (event: SseSyncEvent): void => {
      // Only render when the backend actually shipped the 4-counter summary.
      // Legacy jobs (pre-#322) won't have it — silently drop.
      if (!event.summary) return
      if (!event.jobId || seenJobIdTracker.hasSeen(event.jobId)) return
      if (!hasAnyCounter(event.summary)) return

      seenJobIdTracker.markSeen(event.jobId)

      // "Completed with 0 imported" — the import finished but wrote nothing to
      // the ledger (all rows were duplicates / needs-attention / unprocessable).
      // hasAnyCounter already guaranteed at least one counter is non-zero, so
      // this is a meaningful "nothing landed" outcome, not an empty import.
      const zeroImported = event.summary.importedCount === 0

      // Reconciliation discrepancies are a "warning" outcome — the import
      // landed rows but the post-import balances do not match the source.
      const hasDiscrepancies = (event.summary.reconciliation?.discrepancyCount ?? 0) > 0

      // Part of the requested history never arrived (provider cap). The notice
      // carries a consequence and a remedy, so it also gets more reading time.
      const hasCoverageGap = (event.summary.coverageGaps?.length ?? 0) > 0
      // #264: part of the wallet's chain history never arrived. Same weight as a
      // coverage gap: it carries a consequence and a remedy.
      const hasChainGap = hasChainHistoryGap(event.summary.chainHistoryIncomplete)

      // "Nothing landed because every dropped row was withheld on purpose" is a COMPLETE,
      // CORRECT import (#692): nothing failed, nothing is missing, nothing to act on. Warning
      // about it is the same mislabelling as calling those rows "no procesables". Any other
      // zero-import (duplicates, needs-attention, unprocessable) still warns.
      const zeroImportedNeedsAttention =
        zeroImported &&
        !isImportOutcomeFullyWithheld({
          importedCount: event.summary.importedCount,
          cancelledCount: event.summary.cancelledCount ?? 0,
          failedCount: event.summary.unprocessableCount,
          otherReportedCount:
            event.summary.skippedAsDuplicateCount + event.summary.needsAttentionCount,
        })

      show({
        // Warn when something is unprocessable, nothing was imported for a reason the user must
        // act on, OR the post-import reconciliation found balance discrepancies.
        type:
          event.summary.unprocessableCount > 0 ||
          (event.summary.heldForReviewCount ?? 0) > 0 ||
          zeroImportedNeedsAttention ||
          hasDiscrepancies ||
          event.summary.crossSourceOverlap !== undefined ||
          hasCoverageGap ||
          hasChainGap
            ? 'warning'
            : 'success',
        // `message` is the bold heading; `content` carries the multi-line
        // detail with optional "Ver bandeja" link. Duration 12s gives the
        // user time to click through to the tray before auto-dismiss.
        // The worker-push relay can omit `sourceName`; an unresolved value
        // renders an EMPTY bold heading, so it goes through the same resolver
        // the failure toast and the history panel use.
        message: resolveSyncSourceLabel(event.sourceName, tCommon),
        duration: hasCoverageGap || hasChainGap ? 30_000 : 12_000,
        content: (
          <SyncSummaryToastContent
            summary={event.summary}
            zeroImported={zeroImported}
            onViewTray={() => {
              // Hash anchor lets the tray scroll itself into view on mount.
              navigate('/import#needs-attention')
            }}
          />
        ),
      })
    }

    const unsubscribe = syncClient.on('sync_completed', handleSyncCompleted)
    // Acquire a refcount on the shared SSE connection. Other consumers
    // (ImportProgressIndicator, SyncNotifications) keep it open too — see
    // syncClient.ts header for refcount semantics.
    syncClient.connect()

    return (): void => {
      unsubscribe()
      syncClient.disconnect()
    }
    // We intentionally depend on `show` + `navigate` which are stable refs
    // from their providers — re-running this effect would tear down + re-
    // open the SSE connection which is expensive and unnecessary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
